import React, { useCallback, useEffect, useRef, useState } from "react";
import { PolicyValuesObject } from "../../types/insurance.ts";
import DatePicker from "../Forms/DatePicker/DatePicker.tsx";
import { normalizeDate } from "../../utils/NormalizationUtil.ts";
import moment from "moment";
import { useAuth } from "../../provider/AuthProvider.tsx";

// ─── Shared cell styles (grid-scoped, not exported to formStyles) ─────────────

const cellTextClass = "text-xs font-semibold text-gray-900 dark:text-white";
const cellInputClass = `w-full border-none bg-transparent p-0 focus:outline-none ${cellTextClass}`;
const cellSelectClass = `w-full border-none bg-transparent p-0 focus:outline-none dark:bg-transparent ${cellTextClass}`;
const cellLockedClass =
  "cursor-not-allowed text-xs text-gray-500 dark:text-gray-400";

// ─── Types ────────────────────────────────────────────────────────────────────

type Props = {
  data: PolicyValuesObject[];
  onChange?: (params: {
    rowIndex: number;
    value: any;
    columnId: keyof PolicyValuesObject;
  }) => void;
  stickers: string[];
  greenCards: string[];
  paymentAccounts: string[];
  payPlaces: Record<string, string>;
  agents: Record<string, string>;
  gridTitle?: string;
  addRow: () => void;
  deleteRow: (index: number) => void;
};

// ─── Constants ────────────────────────────────────────────────────────────────

const HEADERS: { name: keyof PolicyValuesObject; header: string }[] = [
  { name: "installmentSequence", header: "№" },
  { name: "installmentDate", header: "Дата вноска" },
  { name: "installmentAmount", header: "Сума" },
  { name: "sticker", header: "Стикер" },
  { name: "stickerAmount", header: "Сума стикер" },
  { name: "paymentLocation", header: "Място плащане" },
  { name: "agentId", header: "Агент" },
  { name: "reportDate", header: "Дата отчет" },
  { name: "paidToAgent", header: "Пл. агент" },
  { name: "twoPercentAmount", header: "2% данък" },
  { name: "greenCardCertificate", header: "СЗК" },
  { name: "documentNumber", header: "№ отчет" },
  { name: "agentPaymentDate", header: "Дата Пл. Агент" },
];

const DEFAULT_COLUMN_WIDTHS: Partial<Record<keyof PolicyValuesObject, number>> =
  {
    installmentSequence: 28,
    installmentDate: 130,
    installmentAmount: 90,
    sticker: 110,
    stickerAmount: 90,
    paymentLocation: 110,
    paidToAgent: 70,
    agentPaymentDate: 130,
    agentId: 130,
    reportDate: 130,
    twoPercentAmount: 90,
    greenCardCertificate: 110,
    documentNumber: 90,
    accountNumber: 120,
  };

const CURRENCY_COLUMNS = new Set<keyof PolicyValuesObject>([
  "installmentAmount",
  "stickerAmount",
  "twoPercentAmount",
]);

// ─── Sub-components ───────────────────────────────────────────────────────────

export const SelectCell = ({
  value,
  options,
  onChange,
}: {
  value: string;
  options: [string, string][] | string[];
  onChange: (v: string) => void;
}) => (
  <select
    value={value}
    onChange={(e) => onChange(e.target.value)}
    className={cellSelectClass}
  >
    {(options as any[]).map((opt, i) => {
      const [val, label] = Array.isArray(opt) ? opt : [opt, opt];
      return (
        <option key={i} value={val}>
          {label}
        </option>
      );
    })}
  </select>
);

const CheckboxCell = ({
  checked,
  disabled,
  onChange,
}: {
  checked: boolean;
  disabled?: boolean;
  onChange?: (v: number) => void;
}) => (
  <div className="flex h-full items-center justify-center">
    <input
      type="checkbox"
      checked={checked}
      disabled={disabled}
      onChange={(e) => onChange?.(e.target.checked ? 1 : 0)}
      className={`h-3 w-3 ${disabled ? "cursor-not-allowed opacity-60" : ""}`}
    />
  </div>
);

const CurrencyCell = ({
  value,
  onChange,
}: {
  value: string | number;
  onChange: (v: string) => void;
}) => {
  const toFormatted = (v: string | number) => {
    const n = Number(v);
    return isNaN(n) ? String(v) : n.toFixed(2);
  };

  const [localValue, setLocalValue] = useState(() => toFormatted(value));
  const [isFocused, setIsFocused] = useState(false);

  useEffect(() => {
    if (!isFocused) setLocalValue(toFormatted(value));
  }, [value, isFocused]);

  return (
    <input
      type="text"
      inputMode="decimal"
      value={localValue}
      onChange={(e) => setLocalValue(e.target.value)}
      onFocus={(e) => {
        setIsFocused(true);
        e.target.select();
      }}
      onBlur={() => {
        setIsFocused(false);
        const formatted = toFormatted(localValue);
        setLocalValue(formatted);
        onChange(formatted);
      }}
      className={`h-5 pl-1 ${cellInputClass}`}
    />
  );
};

// ---- Helpers

const ResizeHandle = ({ onResize }: { onResize: (delta: number) => void }) => {
  const startXRef = useRef<number | null>(null);

  const onMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    startXRef.current = e.clientX;

    const onMouseMove = (ev: MouseEvent) => {
      if (startXRef.current === null) return;
      const delta = ev.clientX - startXRef.current;
      startXRef.current = ev.clientX;
      onResize(delta);
    };

    const onMouseUp = () => {
      startXRef.current = null;
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };

    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
  };

  return (
    <div
      onMouseDown={onMouseDown}
      className="group absolute right-0 top-0 z-10 flex h-full w-3 cursor-col-resize items-center justify-center"
      title="Преоразмери"
    >
      <div className="h-3/4 w-px bg-stone-300 transition-colors group-hover:w-[2px] group-hover:bg-blue-400 dark:bg-stone-600 dark:group-hover:bg-blue-400" />
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

const InstallmentsDataGrid: React.FC<Props> = ({
  data,
  onChange = () => {},
  stickers,
  greenCards,
  paymentAccounts,
  payPlaces,
  agents,
  gridTitle = "Падежи",
  addRow,
  deleteRow,
}) => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [filterText, setFilterText] = useState("");
  const [isSearchVisible, setIsSearchVisible] = useState(false);
  const [selectedRowIndex, setSelectedRowIndex] = useState<number | null>(null);
  const [selectedColumnName, setSelectedColumnName] = useState<
    keyof PolicyValuesObject | null
  >(null);

  // ─── Column widths state ───────────────────────────────────────────────────
  const [columnWidths, setColumnWidths] = useState<
    Record<keyof PolicyValuesObject, number>
  >(
    () =>
      Object.fromEntries(
        HEADERS.map(({ name }) => [name, DEFAULT_COLUMN_WIDTHS[name] ?? 100])
      ) as Record<keyof PolicyValuesObject, number>
  );

  const handleResize = useCallback(
    (colName: keyof PolicyValuesObject, delta: number) => {
      setColumnWidths((prev) => ({
        ...prev,
        [colName]: Math.max(40, (prev[colName] ?? 100) + delta),
      }));
    },
    []
  );

  const { isAdmin } = useAuth();

  useEffect(() => {
    const handleCtrlF = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "f") {
        e.preventDefault();
        setIsSearchVisible((prev) => {
          if (prev) setFilterText("");
          return !prev;
        });
        setTimeout(() => {
          const input = document.querySelector<HTMLInputElement>(
            "input[placeholder='Филтрирай...']"
          );
          input?.focus();
        }, 50);
      }
    };
    window.addEventListener("keydown", handleCtrlF);
    return () => window.removeEventListener("keydown", handleCtrlF);
  }, []);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isFullscreen) setIsFullscreen(false);
    };
    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [isFullscreen]);

  const filteredData = filterText
    ? data.filter((item) =>
        Object.entries(item).some(([key, v]) => {
          let display = v !== undefined && v !== null ? String(v) : "";

          if (key === "agentId") {
            display = agents[display] ?? display;
          } else if (key === "paymentLocation") {
            display = payPlaces[display] ?? display;
          } else if (key === "paidToAgent" || key === "paidInBank") {
            display = v === 1 ? "да" : "не";
          }

          return display.toLowerCase().includes(filterText.toLowerCase());
        })
      )
    : data;

  const handleCellChange = useCallback(
    (rowIndex: number, columnId: keyof PolicyValuesObject, value: any) => {
      onChange({ rowIndex, columnId, value });
    },
    [onChange]
  );

  const handleCellKeyDown = useCallback(
    (
      e: React.KeyboardEvent<HTMLTableCellElement>,
      rowIndex: number,
      columnName: keyof PolicyValuesObject
    ) => {
      if (columnName === "paidInBank" || columnName === "bankPaymentDate")
        return;
      if (e.key !== "Tab") return;
      e.preventDefault();

      const activeEl = document.activeElement as HTMLElement | null;
      if (activeEl && e.currentTarget.contains(activeEl)) {
        activeEl.blur();
      }

      const navigableCols = HEADERS.length - 2;
      const numRows = filteredData.length;
      let col = HEADERS.findIndex((h) => h.name === columnName);
      let row = rowIndex;
      const delta = e.key === "Tab" && e.shiftKey ? -1 : 1;

      col += delta;
      if (col >= navigableCols) {
        col = 0;
        row = (row + 1) % numRows;
      } else if (col < 0) {
        col = navigableCols - 1;
        row = (row - 1 + numRows) % numRows;
      }

      setSelectedRowIndex(row);
      setSelectedColumnName(HEADERS[col].name);

      setTimeout(() => {
        const cell = document.querySelector<HTMLElement>(
          `[data-row-index="${row}"][data-column-name="${HEADERS[col].name}"]`
        );
        const target =
          cell?.querySelector<HTMLElement>('input, select, [tabindex="0"]') ??
          cell;
        target?.focus();
      }, 0);
    },
    [filteredData]
  );

  const downloadCSV = useCallback(() => {
    const rows = [
      HEADERS.map((h) => h.header).join(","),
      ...data.map((row) =>
        HEADERS.map(({ name }) => {
          if (name === "paidInBank") return row[name] === 1 ? "Да" : "Не";
          return String(row[name] ?? "");
        }).join(",")
      ),
    ];
    const blob = new Blob([rows.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    Object.assign(document.createElement("a"), {
      href: url,
      download: `Падежи-${moment().format("DD/MM/YYYY")}.csv`,
    }).click();
    URL.revokeObjectURL(url);
  }, [data]);

  const renderCell = useCallback(
    (
      row: PolicyValuesObject,
      idx: number,
      key: keyof PolicyValuesObject,
      isLocked: boolean
    ) => {
      const val = row[key] ?? "";
      const change = (v: any) => handleCellChange(idx, key, v);

      if (isLocked) {
        let display = val !== undefined && val !== null ? String(val) : "";
        if (key === "paidToAgent" || key === "paidInBank") {
          return <CheckboxCell checked={val === 1} disabled />;
        }
        if (key === "bankPaymentDate") {
          display = val ? normalizeDate(String(val)) : "Не е платено";
        }
        if (key === "agentId") {
          display = agents[String(val)] ?? String(val);
        }
        if (key === "paymentLocation") {
          display = payPlaces[String(val)] ?? String(val);
        }
        if (key === "sticker") {
          display = String(val);
        }
        if (CURRENCY_COLUMNS.has(key)) {
          const n = Number(val);
          display = isNaN(n) ? String(val) : n.toFixed(2);
        }
        return <span className={cellLockedClass}>{display}</span>;
      }

      if (key === "installmentSequence" || key === "documentNumber") {
        return (
          <span className={`cursor-not-allowed ${cellTextClass} opacity-60`}>
            {val !== undefined && val !== null ? String(val) : ""}
          </span>
        );
      }
      if (key === "bankPaymentDate") {
        return (
          <span className={`cursor-not-allowed ${cellTextClass}`}>
            {val ? normalizeDate(String(val)) : "Не е платено"}
          </span>
        );
      }
      if (key === "paidToAgent")
        return <CheckboxCell checked={val === 1} onChange={change} />;
      if (key === "paidInBank")
        return <CheckboxCell checked={val === 1} disabled />;
      if (key === "installmentDate" || key === "reportDate") {
        return (
          <DatePicker
            name={key}
            value={String(val)}
            onChange={(e: any) => change(e.target.value)}
            classNameAdditional="!border-none !p-0 !bg-transparent text-xs font-semibold dark:text-white"
          />
        );
      }
      if (CURRENCY_COLUMNS.has(key)) {
        return (
          <CurrencyCell value={val as string | number} onChange={change} />
        );
      }
      if (key === "accountNumber") {
        const opts =
          typeof val === "string" && val && !paymentAccounts.includes(val)
            ? [val, ...paymentAccounts]
            : paymentAccounts;
        return (
          <SelectCell value={String(val)} options={opts} onChange={change} />
        );
      }
      if (key === "sticker") {
        const opts =
          typeof val === "string" && val && !stickers.includes(val)
            ? [val, ...stickers]
            : stickers;
        return (
          <SelectCell value={String(val)} options={opts} onChange={change} />
        );
      }
      if (key === "greenCardCertificate") {
        const opts =
          typeof val === "string" && val && !greenCards.includes(val)
            ? [val, ...greenCards]
            : greenCards;
        return (
          <select
            value={String(val)}
            onChange={(e) => change(e.target.value)}
            className={cellSelectClass}
          >
            <option value="">Избери</option>
            {opts.map((g, i) => (
              <option key={i} value={g}>
                {g}
              </option>
            ))}
          </select>
        );
      }
      if (key === "agentId")
        return (
          <SelectCell
            value={String(val)}
            options={Object.entries(agents)}
            onChange={change}
          />
        );
      if (key === "paymentLocation")
        return (
          <SelectCell
            value={String(val)}
            options={Object.entries(payPlaces)}
            onChange={change}
          />
        );

      if (key === "agentPaymentDate") {
        const raw = String(val);
        if (raw.includes("/")) {
          const slashIdx = raw.indexOf("/");
          const num = raw.slice(0, slashIdx);
          const after = raw.slice(slashIdx + 1);
          const date = /^\d{8}$/.test(after) ? normalizeDate(after) : after;
          return (
            <span className={`cursor-not-allowed ${cellTextClass} opacity-60`}>
              <span>{num}</span>
              {" | "}
              <span>{date}</span>
            </span>
          );
        }
        return <span className={cellLockedClass}>{raw}</span>;
      }
      return (
        <input
          type="text"
          value={String(val)}
          onChange={(e) => change(e.target.value)}
          className={cellInputClass}
        />
      );
    },
    [handleCellChange, stickers, greenCards, paymentAccounts, payPlaces, agents]
  );

  const TableContent = (
    // `table-fixed` + explicit px widths on <th> is what makes column resizing work.
    // The <colgroup> drives the actual widths; <th> widths are just the initial hint.
    <div className="w-full overflow-x-auto">
      <table
        className="dark:border-strokedark border border-stone-300"
        style={{
          tableLayout: "fixed",
          width: `${HEADERS.reduce(
            (sum, { name }) => sum + (columnWidths[name] ?? 100),
            0
          )}px`,
        }}
      >
        {/* colgroup is the canonical way to control column widths in a table */}
        <colgroup>
          {HEADERS.map(({ name }) => (
            <col
              key={name}
              style={{ width: `${columnWidths[name] ?? 100}px` }}
            />
          ))}
        </colgroup>

        <thead className="dark:bg-meta-4 bg-gray-100 text-gray-700 dark:text-white">
          <tr>
            {HEADERS.map(({ name, header }) => (
              <th
                key={name}
                className="dark:border-strokedark relative select-none border border-stone-300 px-1 py-0.5 text-center text-xs font-bold"
                style={{ width: `${columnWidths[name] ?? 100}px` }}
              >
                <span className="block truncate">{header}</span>
                <ResizeHandle
                  onResize={(delta) =>
                    handleResize(name as keyof PolicyValuesObject, delta)
                  }
                />
              </th>
            ))}
          </tr>
        </thead>

        <tbody>
          {filteredData.length === 0 ? (
            <tr>
              <td
                colSpan={HEADERS.length}
                className="dark:border-strokedark dark:bg-boxdark-2 border border-stone-300 bg-stone-100 p-2 text-center text-xs font-semibold text-gray-500 dark:text-gray-400"
              >
                Няма записи
              </td>
            </tr>
          ) : (
            filteredData.map((row, idx) => (
              <tr
                key={idx}
                onClick={() => {
                  setSelectedRowIndex(idx);
                  setSelectedColumnName(null);
                }}
                className={[
                  "dark:border-stroke-dark cursor-pointer border border-stone-300 transition-colors",
                  !!row.documentNumber && !isAdmin
                    ? "cursor-not-allowed bg-gray-100 opacity-70 dark:bg-gray-800/60"
                    : idx % 2 === 0
                    ? "dark:bg-box-dark-2 bg-stone-100"
                    : "dark:bg-box-dark bg-white",
                  "dark:hover:bg-primary-light hover:bg-blue-50",
                  selectedRowIndex === idx
                    ? "border-primary-light/50 dark:!bg-primary-light/50"
                    : "",
                ].join(" ")}
              >
                {HEADERS.map(({ name }) => (
                  <td
                    key={name}
                    tabIndex={0}
                    data-row-index={idx}
                    data-column-name={name}
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedRowIndex(idx);
                      setSelectedColumnName(name);
                    }}
                    onKeyDown={(e) => handleCellKeyDown(e, idx, name)}
                    className="overflow-hidden px-1 py-0.5"
                    style={
                      selectedRowIndex === idx && selectedColumnName === name
                        ? {
                            boxShadow:
                              "bg-primary-light opacity-50 0px 0px 0px 2px inset",
                          }
                        : { border: "1px solid #d1d5db" }
                    }
                  >
                    {renderCell(
                      row,
                      idx,
                      name,
                      !!row.documentNumber && !isAdmin
                    )}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );

  const Toolbar = (
    <div className="mb-2 flex w-full items-center justify-between">
      <h2 className="text-sm font-bold text-gray-900 dark:text-white">
        {gridTitle}
      </h2>
      <div className="flex items-center gap-1">
        {[
          { icon: "fa-plus", title: "Добави", onClick: addRow },
          {
            icon: "fa-minus",
            title: "Изтрий",
            onClick: () => {
              if (selectedRowIndex !== null) {
                deleteRow(selectedRowIndex);
                setSelectedRowIndex(null);
                setSelectedColumnName(null);
              } else {
                alert("Моля, изберете ред за изтриване.");
              }
            },
          },
          {
            icon: "fa-search",
            title: "Търсене",
            onClick: () => {
              setIsSearchVisible((v) => !v);
              if (isSearchVisible) setFilterText("");
            },
          },
          { icon: "fa-download", title: "Изтегли CSV", onClick: downloadCSV },
          {
            icon: "fa-expand",
            title: "Цял екран",
            onClick: () => setIsFullscreen(true),
          },
        ].map(({ icon, title, onClick }) => (
          <button
            key={icon}
            title={title}
            onClick={onClick}
            className="bg-primary hover:bg-primary-dark flex h-6 w-6 items-center justify-center rounded text-white transition-colors"
          >
            <i className={`fa-solid ${icon} text-xs`} />
          </button>
        ))}
        <div
          className={`overflow-hidden transition-all duration-300 ${
            isSearchVisible ? "ml-1 w-40 opacity-100" : "w-0 opacity-0"
          }`}
        >
          <input
            type="text"
            placeholder="Филтрирай..."
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
            className="dark:bg-boxdark-2 w-full rounded p-1 text-xs font-semibold text-gray-900 dark:text-white dark:placeholder-gray-400"
          />
        </div>
      </div>
    </div>
  );

  return (
    <>
      {!isFullscreen && (
        <div className="border-stroke dark:border-strokedark dark:bg-boxdark mt-4 w-full max-w-full rounded-xl border bg-white p-4 shadow-md">
          {Toolbar}
          <div className="w-full overflow-x-auto">{TableContent}</div>
        </div>
      )}
      {isFullscreen && (
        <>
          <div
            className="fixed inset-0 z-[9998] bg-black/50"
            onClick={() => setIsFullscreen(false)}
          />
          <div className="fixed left-1/2 top-1/2 z-[9999] flex h-[95vh] w-[95vw] -translate-x-1/2 -translate-y-1/2 flex-col rounded-lg bg-white p-6 dark:bg-gray-900">
            <div className="mb-2 flex items-center justify-between">
              <h2 className="text-sm font-bold text-gray-900 dark:text-white">
                {gridTitle}
              </h2>
              <button
                onClick={() => setIsFullscreen(false)}
                className="bg-primary hover:bg-primary-dark flex h-6 w-6 items-center justify-center rounded text-white"
                title="Изход от цял екран"
              >
                <i className="fa-solid fa-xmark text-xs" />
              </button>
            </div>
            <div className="flex-grow overflow-x-auto">{TableContent}</div>
          </div>
        </>
      )}
    </>
  );
};

export default InstallmentsDataGrid;
